import { useEffect } from 'react'
import { useAppStore } from '@core/store'
import { applyLevelsOverlay, applyGridsOverlay } from '@viewer/lib/levelsAndGrids'
import { applyOriginMarkerOverlay } from '@viewer/lib/originMarker'
import { applyBasemap3D } from '@viewer/map/basemap3d'

// ---------------------------------------------------------------------------
// Host (sem UI) dos overlays 3D: Levels, Grids IFC, marcador de origem e
// chão OSM. Vive no ViewerPage, que está SEMPRE montado enquanto o viewer
// existe: os toggles (ribbon, MapPanel, mobile) apenas mudam estado no
// store e este efeito é o ÚNICO chamador dos apply*, evitando builds
// async entrelaçadas (cada apply usa token de reentrância e limpa quando
// o estado é false).
//
// Historicamente o efeito vivia no VistaTab do ribbon, que só está
// montado com esse tab ativo: ligar "Ver no 3D" no MapPanel com outro
// tab à frente não fazia nada. Daí a extração para aqui.
// ---------------------------------------------------------------------------

export default function OverlayEffectsHost() {
  const levelsOverlay = useAppStore((s) => s.levelsOverlay)
  const gridsOverlay = useAppStore((s) => s.gridsOverlay)
  const gridsStoreyFilter = useAppStore((s) => s.gridsStoreyFilter)
  const originMarkerOverlay = useAppStore((s) => s.originMarkerOverlay)
  const originMarkerKinds = useAppStore((s) => s.originMarkerKinds)
  const originMarkerFocus = useAppStore((s) => s.originMarkerFocus)
  const geoBaselines = useAppStore((s) => s.geoBaselines)
  const geoOverrides = useAppStore((s) => s.geoOverrides)
  const modelCount = useAppStore((s) => s.models.length)
  const basemap3dOn = useAppStore((s) => s.basemap3dOn)
  const basemapGroundH = useAppStore((s) => s.basemapGroundH)
  const basemapTerrainOn = useAppStore((s) => s.basemapTerrainOn)
  const basemapExtent = useAppStore((s) => s.basemapExtent)
  const crsMetaAll = useAppStore((s) => s.crsMeta)
  const crsOverridesAll = useAppStore((s) => s.crsOverrides)
  // Reaplica quando o conjunto de modelos muda (novo bbox / IfcGrids),
  // quando um toggle vira, quando o filtro de storeys ou de origens muda
  // e quando a posição geo / CRS é editada (o marcador e o chão seguem).
  useEffect(() => {
    // Sempre apply(state): cada apply limpa quando o estado é false, por
    // isso o host é o único chamador e o desligar também passa por aqui.
    void applyLevelsOverlay(levelsOverlay)
    void applyGridsOverlay(gridsOverlay)
    void applyBasemap3D(basemap3dOn)
  }, [
    modelCount,
    levelsOverlay,
    gridsOverlay,
    gridsStoreyFilter,
    geoBaselines,
    geoOverrides,
    basemap3dOn,
    basemapGroundH,
    basemapTerrainOn,
    basemapExtent,
    crsMetaAll,
    crsOverridesAll,
  ])
  // O marcador de origens à parte: pôr uma origem em foco ("Ir") só o
  // redesenha a ele, sem refazer o chão, os levels e as grelhas.
  useEffect(() => {
    // O modelo da origem em foco saiu: volta a mostrar todas.
    const s = useAppStore.getState()
    if (originMarkerFocus && !s.models.some((m) => m.id === originMarkerFocus.modelId)) {
      s.setOriginMarkerFocus(null)
      return
    }
    void applyOriginMarkerOverlay(originMarkerOverlay)
  }, [
    modelCount,
    originMarkerOverlay,
    originMarkerKinds,
    originMarkerFocus,
    geoBaselines,
    geoOverrides,
  ])
  return null
}
